/**
 * Epic 278 Phase 2 Slice C — Case timeline + Measurement summary builders.
 * Pure helpers over SafetyDashboardRow; never invent unsupported metrics.
 */
import type { SafetyDashboardRow, WorkflowImpactSummary } from '../../services/reportsApi';

export type CaseTimelineEvent = {
  id: string;
  timestamp: string | null;
  action: string;
  actor: string | null;
  recordLabel: string | null;
  resultingState: string | null;
};

export type MeasurementSummaryView = {
  baselineScan: string | null;
  followUpScan: string | null;
  coverageComparability: string | null;
  measuredDifference: string | null;
  interpretationState: string | null;
  effectivenessReviewOutstanding: boolean;
  effectivenessReviewLabel: string;
  emptyReason: string | null;
};

function trimText(value: unknown): string | null {
  const text = String(value ?? '').trim();
  return text || null;
}

function formatWhen(value?: string | null): string | null {
  const raw = trimText(value);
  if (!raw) return null;
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return null;
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(parsed);
}

function formatWindow(window?: { start?: string | null; end?: string | null } | null): string | null {
  if (!window) return null;
  const start = formatWhen(window.start);
  const end = formatWhen(window.end);
  if (start && end) return `${start} → ${end}`;
  return start || end;
}

function formatSignedScanLabel(row: SafetyDashboardRow): string | null {
  const name = trimText(row.latest_scan_name) || trimText(row.safety_scan_title) || 'Signed Safety Scan';
  const when = formatWhen(row.latest_scan_signed_at);
  return when ? `${name} (${when})` : name;
}

function pushEvent(
  events: CaseTimelineEvent[],
  event: Omit<CaseTimelineEvent, 'id'> & { id?: string },
): void {
  const action = trimText(event.action);
  if (!action) return;
  // Prefer events with a timestamp; allow undated state markers only when no timed twin exists.
  events.push({
    id: event.id || `${action}:${event.timestamp || 'undated'}`,
    timestamp: event.timestamp || null,
    action,
    actor: trimText(event.actor),
    recordLabel: trimText(event.recordLabel),
    resultingState: trimText(event.resultingState),
  });
}

/**
 * Build a chronological case/activity timeline from dashboard row fields only.
 * Missing lifecycle steps are omitted rather than fabricated.
 */
export function buildCaseTimelineEvents(row: SafetyDashboardRow | null | undefined): CaseTimelineEvent[] {
  if (!row) return [];
  const events: CaseTimelineEvent[] = [];
  const impact = (row.impact_summary || null) as WorkflowImpactSummary | null;
  const caseLabel = trimText(row.safety_case_title) || trimText(row.finding_id) || trimText(row.safety_case_id);
  const patternLabel = trimText(row.risk_pattern_label) || trimText(row.risk_label) || 'Risk Pattern';

  pushEvent(events, {
    id: 'risk-pattern-identified',
    timestamp: trimText(row.first_detected_at),
    action: 'Risk Pattern identified',
    actor: null,
    recordLabel: patternLabel,
    resultingState: trimText(row.customer_risk_pattern_work_state) || trimText(row.review_status_label),
  });

  if (row.representative_evidence_count != null && Number(row.representative_evidence_count) > 0) {
    pushEvent(events, {
      id: 'evidence-available',
      timestamp: trimText(row.latest_observation_at) || trimText(row.first_detected_at),
      action: 'Evidence available for review',
      actor: null,
      recordLabel: `${row.representative_evidence_count} reviewable episode${
        Number(row.representative_evidence_count) === 1 ? '' : 's'
      }`,
      resultingState: trimText(row.latest_review_status_label) || trimText(row.review_status_label),
    });
  }

  if (row.finding_saved || trimText(row.safety_case_id) || trimText(row.finding_id)) {
    pushEvent(events, {
      id: 'safety-case-confirmed',
      timestamp: trimText(row.finding_saved_at) || trimText(row.report_created_at),
      action: 'Safety Case confirmed',
      actor: trimText(row.finding_saved_by),
      recordLabel: caseLabel,
      resultingState:
        trimText(row.customer_control_stage)
        || trimText(row.safety_case_status_label)
        || trimText(row.safety_case_status),
    });
  }

  if (trimText(row.corrective_action_status) || trimText(row.corrective_action_status_label)) {
    pushEvent(events, {
      id: 'control-action',
      timestamp: trimText(impact?.corrective_action_timestamp) || trimText(row.updated_at),
      action: 'Control / corrective action recorded',
      actor: trimText(row.followup_owner) || trimText(row.owner_name) || trimText(row.owner),
      recordLabel: trimText(impact?.intervention_reference),
      resultingState:
        trimText(row.corrective_action_status_label) || trimText(row.corrective_action_status),
    });
  }

  const signedAt = trimText(row.latest_scan_signed_at) || trimText(row.report_finalized_at);
  if (signedAt || /signed/i.test(String(row.latest_scan_status || row.signoff_status || ''))) {
    pushEvent(events, {
      id: 'scan-signed',
      timestamp: signedAt,
      action: 'Safety Scan signed',
      actor: null,
      recordLabel: formatSignedScanLabel(row),
      resultingState:
        trimText(row.customer_record_state)
        || trimText(row.customer_scan_workflow_state)
        || trimText(row.signoff_status)
        || 'Signed',
    });
  } else if (trimText(row.latest_scan_id) || trimText(row.latest_scan_name) || trimText(row.active_draft_report_id)) {
    pushEvent(events, {
      id: 'scan-in-progress',
      timestamp: trimText(row.latest_scan_scope_end) || trimText(row.updated_at),
      action: 'Follow-up Scan recorded',
      actor: null,
      recordLabel: trimText(row.latest_scan_name) || trimText(row.safety_scan_title) || 'Safety Scan',
      resultingState:
        trimText(row.customer_scan_workflow_state)
        || trimText(row.latest_scan_status)
        || trimText(row.customer_scan_result),
    });
  }

  if (Array.isArray(row.scan_history)) {
    row.scan_history.slice(0, 3).forEach((scan, index) => {
      const scanSigned = trimText(scan.signed_at);
      if (!scanSigned && !trimText(scan.scan_name) && !trimText(scan.scan_id)) return;
      // Skip duplicate of latest signed scan already emitted.
      if (signedAt && scanSigned && scanSigned === signedAt) return;
      pushEvent(events, {
        id: `scan-history-${index}-${scan.scan_id || scan.report_id || index}`,
        timestamp: scanSigned || trimText(scan.scope_end),
        action: scanSigned ? 'Prior Safety Scan signed' : 'Prior Safety Scan recorded',
        actor: null,
        recordLabel: trimText(scan.scan_name) || 'Safety Scan',
        resultingState: trimText(scan.signature_state) || trimText(scan.status),
      });
    });
  }

  if (
    row.has_new_activity_since_report
    || (typeof row.new_activity_count === 'number' && row.new_activity_count > 0)
    || row.workflow_changed_since_signoff
  ) {
    const count = typeof row.new_activity_count === 'number' ? row.new_activity_count : null;
    pushEvent(events, {
      id: 'new-activity',
      timestamp: trimText(row.latest_observation_at) || trimText(row.updated_at),
      action: 'New activity detected after sign-off',
      actor: null,
      recordLabel: count != null ? `${count} new observation${count === 1 ? '' : 's'}` : 'New activity',
      resultingState: trimText(row.customer_attention_work_state) || 'New activity',
    });
  }

  if (trimText(row.impact_status_label) || trimText(row.impact_measurement_status) || impact?.display_headline) {
    pushEvent(events, {
      id: 'effectiveness-review',
      timestamp: trimText(row.status_computed_at) || trimText(row.updated_at),
      action: 'Effectiveness / measurement reviewed',
      actor: trimText(impact?.waived_by),
      recordLabel: trimText(impact?.display_headline) || trimText(row.impact_status_label),
      resultingState:
        trimText(impact?.display_status)
        || trimText(row.impact_status_label)
        || trimText(row.impact_measurement_status)
        || trimText(impact?.measurement_status),
    });
  }

  const dated = events.filter((event) => event.timestamp);
  const undated = events.filter((event) => !event.timestamp);
  dated.sort((a, b) => {
    const aTime = new Date(String(a.timestamp)).getTime();
    const bTime = new Date(String(b.timestamp)).getTime();
    if (Number.isNaN(aTime) || Number.isNaN(bTime)) return 0;
    return aTime - bTime;
  });
  // Keep undated markers after dated chronology so sparse rows still show known states.
  return [...dated, ...undated];
}

function formatNumericDelta(value: number | null | undefined, suffix: string): string | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  const rounded = Math.abs(value) >= 10 ? Math.round(value) : Math.round(value * 10) / 10;
  const sign = rounded > 0 ? '+' : '';
  return `${sign}${rounded}${suffix}`;
}

function resolveEffectivenessOutstanding(row: SafetyDashboardRow, impact: WorkflowImpactSummary | null): boolean {
  const stage = String(row.customer_control_stage || '').toLowerCase();
  if (stage.includes('effectiveness review')) return true;
  const impactStatus = String(
    row.impact_measurement_status || impact?.measurement_status || impact?.status || '',
  ).toLowerCase();
  if (impactStatus.includes('pending') || impactStatus === 'needs_review') return true;
  if (row.followup_required === true) return true;
  if (impact?.status === 'pending') return true;
  return false;
}

/**
 * Build measurement summary fields for drawer/case detail. Omits unsupported stats.
 */
export function buildMeasurementSummary(row: SafetyDashboardRow | null | undefined): MeasurementSummaryView {
  if (!row) {
    return {
      baselineScan: null,
      followUpScan: null,
      coverageComparability: null,
      measuredDifference: null,
      interpretationState: null,
      effectivenessReviewOutstanding: false,
      effectivenessReviewLabel: 'Not applicable',
      emptyReason: 'Measurement summary is not available for this item.',
    };
  }

  const impact = (row.impact_summary || null) as WorkflowImpactSummary | null;
  const baselineScan =
    formatWindow(impact?.baseline_window)
    || (trimText(row.latest_scan_scope_start) && trimText(row.latest_scan_scope_end)
      ? `${formatWhen(row.latest_scan_scope_start)} → ${formatWhen(row.latest_scan_scope_end)}`
      : null)
    || (trimText(row.latest_scan_name) && !trimText(row.latest_scan_signed_at)
      ? trimText(row.latest_scan_name)
      : null);

  const followUpFromImpact = formatWindow(impact?.followup_window) || formatWindow(impact?.post_action_window);
  const followUpScan =
    followUpFromImpact
    || (trimText(row.latest_scan_signed_at)
      ? formatSignedScanLabel(row)
      : trimText(row.latest_scan_name));

  const coverageComparability =
    trimText(row.impact_status_label)
    || trimText(impact?.display_status)
    || trimText(impact?.measurement_status)
    || trimText(row.impact_measurement_status)
    || trimText(row.measurement_trust_level)
    || trimText(row.customer_scan_result);

  let measuredDifference: string | null = null;
  if (typeof impact?.percent_reduction === 'number' && Number.isFinite(impact.percent_reduction)) {
    const reduction = Math.round(Number(impact.percent_reduction) * 10) / 10;
    measuredDifference = `${reduction}% measured reduction`;
  } else if (typeof row.exposure_change_percent === 'number' && Number.isFinite(row.exposure_change_percent)) {
    const delta = formatNumericDelta(Number(row.exposure_change_percent), '% exposure change');
    const direction = trimText(row.exposure_change_direction);
    measuredDifference = direction
      ? `${delta} (${String(direction).replace(/_/g, ' ')})`
      : delta;
  } else if (trimText(row.exposure_change_direction)) {
    measuredDifference = `Direction: ${String(row.exposure_change_direction).replace(/_/g, ' ')}`;
  } else if (
    typeof impact?.absolute_event_reduction === 'number'
    && Number.isFinite(impact.absolute_event_reduction)
  ) {
    measuredDifference = `${impact.absolute_event_reduction} event reduction`;
  }

  const interpretationState =
    trimText(impact?.display_headline)
    || trimText(impact?.display_summary)
    || trimText(row.impact_status_label)
    || trimText(row.customer_scan_result)
    || trimText(row.recommended_next_step);

  const outstanding = resolveEffectivenessOutstanding(row, impact);
  const hasAny =
    Boolean(baselineScan || followUpScan || coverageComparability || measuredDifference || interpretationState);

  return {
    baselineScan,
    followUpScan,
    coverageComparability,
    measuredDifference,
    interpretationState,
    effectivenessReviewOutstanding: outstanding,
    effectivenessReviewLabel: outstanding
      ? 'Human effectiveness review remains outstanding'
      : (impact?.status === 'completed' || /complete|closed|monitoring/i.test(String(row.customer_control_stage || ''))
        ? 'Effectiveness review not outstanding on this summary'
        : 'Effectiveness review status not indicated on this summary'),
    emptyReason: hasAny
      ? null
      : 'Baseline and follow-up measurement details are not yet recorded on this summary.',
  };
}

export function formatTimelineTimestamp(value?: string | null): string {
  return formatWhen(value) || 'Timing not recorded';
}
